'use client';

import { Stack } from '@unifyapps/ui/components/Stack';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { useThemeOverrideContext } from '@unifyapps/ui/providers/ThemeOverrideContext';
import { clsx } from 'clsx';
import map from 'lodash/map';
import { forwardRef, useCallback } from 'react';
import { UA_PLATFORM_SIDE_NAV_CUSTOM_SCROLLBAR } from '../../components/platform/constants';
import { DATA_NAV_ROOT_ATTRIBUTE } from '../../constants';
import { isSelectionVariantStroke } from '../../helpers';
import { appSideNavRootVariants } from '../../styles';
import type { SideNavProps } from '../../types';
import { getIsNavigationEmpty } from '../../utils/emptyNavigation';
import AppInfo from './AppInfo';
import { useSideNavigation } from './hooks/useSideNavigation';
import NavigationItemComponent from './NavigationItemComponent';

const DARK_BORDER_STYLE = {
  '--border-tertiary': 'var(--utility-gray-100)',
} as React.CSSProperties;

type WrapperProps = {
  children: React.ReactNode;
  isHorizontalUpfront: boolean;
};

function SideNavigationWrapper({ children, isHorizontalUpfront }: WrapperProps) {
  if (!isHorizontalUpfront) {
    return <>{children}</>;
  }

  return (
    <Stack className="h-full w-fit" direction="column">
      {children}
    </Stack>
  );
}

export const SideNavigation = forwardRef<HTMLDivElement, SideNavProps>((props, ref) => {
  const { sideNavRef, slots } = props;

  const { scheme } = useThemeOverrideContext();

  const {
    adaptedPrimaryNavItems,
    adaptedSecondaryNavItems,
    brandNavigationItem,
    className,
    expanded,
    hoverProps,
    isHorizontal,
    isLabelExpandable,
    isUserNavItemPresent,
    rootClassName,
    style,
    activeMenuItemId,
    setActiveMenuItemId,
    selectionVariant,
    navItemsGapClassName,
    primaryNavStyles,
    isHorizontalUpfront,
    app,
    activePathnames,
    slotProps,
    setExpanded,
    isPlatformSideNav,
    isTopPositioned,
    primaryNavRef,
    secondaryNavRef,
    secondaryNavStyles,
    labelVariant,
    userMenuItem,
    position,
  } = useSideNavigation(props);

  const isNavigationEmpty = getIsNavigationEmpty({
    primary: adaptedPrimaryNavItems,
    secondary: adaptedSecondaryNavItems,
  });

  const topSlot = slots?.top
    ? slots.top({
        expanded,
        setExpanded,
        navItemPopover: slotProps.navigationItem.navItemPopover,
        setNavItemPopover: slotProps.navigationItem.setNavItemPopover,
      })
    : null;

  // platform rail: clicking any empty (non-interactive) part toggles expand/collapse, not just the
  // header button. Skip clicks that land on a nav item / control so they keep their own behaviour.
  const handleRailToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // modals/popovers rendered as React children of the rail portal their DOM out (to the
      // container element / body), but their synthetic click still bubbles up the React tree to
      // this handler. ignore clicks whose real DOM target sits outside the rail so e.g. typing in
      // the project-editor modal does not re-expand the collapsed rail.
      if (!event.currentTarget.contains(event.target as Node)) {
        return;
      }
      // popovers opened with `useAnchorAsContainer` (the user menu) render INSIDE the rail instead
      // of portalling out, so the guard above lets them through — anything within one is the
      // menu's business, not the rail's.
      if (
        (event.target as HTMLElement).closest(
          'a,button,[role="button"],input,textarea,select,[contenteditable="true"],.ua-popover',
        )
      ) {
        return;
      }
      setExpanded(!expanded);
    },
    [expanded, setExpanded],
  );

  if (isNavigationEmpty) return null;

  return (
    <Stack
      aria-label="side-nav"
      // platform nav is in-flow (w-fit) so expanding the inner rail pushes the flex-1 content;
      // other navs keep a fixed reserved basis since they overlay.
      className={clsx(
        'relative h-full',
        isPlatformSideNav ? 'w-fit shrink-0' : 'basis-platformSidebarWidth',
        rootClassName,
      )}
      ref={ref}
      style={scheme === 'dark' ? DARK_BORDER_STYLE : {}}
    >
      <Stack
        className={slowCn(
          appSideNavRootVariants({
            expanded,
            isLabelExpandable,
            isTopPositioned,
            isPlatformSideNav,
          }),
          className,
          {
            'pb-lg': !isUserNavItemPresent,
          },
          'h-full',
          { 'w-full': isTopPositioned },
          { 'items-center': isHorizontal },
          // named group so the header's logo→toggle swap reveals on rail hover/focus only
          // (a plain `group` would collide with the screen-level `.group` on <body>).
          { 'group/sidenav': isPlatformSideNav },
          // whole green rail is click-to-toggle (handleRailToggle) and every child is interactive,
          // so show the pointer cursor across the rail — matches Figma's cursor-pointer nav root.
          { 'cursor-pointer': isPlatformSideNav },
        )}
        direction={isTopPositioned ? 'row' : 'column'}
        onClick={isPlatformSideNav ? handleRailToggle : undefined}
        ref={sideNavRef}
        {...{ [DATA_NAV_ROOT_ATTRIBUTE]: 'side' }}
        {...hoverProps}
        style={style}
      >
        {app ? (
          <AppInfo
            app={app}
            className={isTopPositioned ? 'items-center' : ''}
            expanded={expanded}
            labelVariant={labelVariant}
            logoNavigation={brandNavigationItem}
            position={position}
            setExpanded={setExpanded}
          />
        ) : null}
        {/* oxlint-disable-next-line no-nested-ternary */}
        {topSlot ? isPlatformSideNav ? <Stack className="mt-lg">{topSlot}</Stack> : topSlot : null}
        <SideNavigationWrapper isHorizontalUpfront={isHorizontalUpfront}>
          <>
            {/**  Primary Nav Items  */}
            <Stack
              className={slowCn(
                'min-h-0 overflow-auto overflow-x-hidden',
                // oxlint-disable-next-line no-nested-ternary
                isPlatformSideNav
                  ? 'gap-md'
                  : isSelectionVariantStroke(selectionVariant)
                    ? 'gap-lg'
                    : 'gap-xs',
                !isPlatformSideNav ? navItemsGapClassName : undefined,
                { 'px-[10px] pb-md': isPlatformSideNav },
                { 'mt-lg': isPlatformSideNav && topSlot },
                { 'mt-sm': isPlatformSideNav && !topSlot },
                { [UA_PLATFORM_SIDE_NAV_CUSTOM_SCROLLBAR]: isPlatformSideNav },
              )}
              direction={isTopPositioned ? 'row' : 'column'}
              ref={primaryNavRef}
              style={primaryNavStyles}
            >
              {map(adaptedPrimaryNavItems, (item) => {
                return (
                  <NavigationItemComponent
                    activeMenuItemId={activeMenuItemId}
                    activePathnames={activePathnames}
                    item={item}
                    key={item.id}
                    setActiveMenuItemId={setActiveMenuItemId}
                    slotProps={slotProps}
                  />
                );
              })}
            </Stack>

            {/**  Secondary Nav Items  */}
            <Stack
              className={clsx(
                // for platform nav, use hidden x-axis; for others, use visible to show notification badges
                isPlatformSideNav ? 'overflow-x-hidden' : '',

                isTopPositioned ? 'ml-auto' : 'mt-auto',
                // oxlint-disable-next-line no-nested-ternary
                isPlatformSideNav
                  ? 'gap-md'
                  : isSelectionVariantStroke(selectionVariant)
                    ? 'gap-lg'
                    : 'gap-xs',
                // oxlint-disable-next-line no-nested-ternary
                isPlatformSideNav ? undefined : isTopPositioned ? 'gap-lg' : navItemsGapClassName,
                { 'px-[10px] pt-lg pb-lg': isPlatformSideNav },
                { [UA_PLATFORM_SIDE_NAV_CUSTOM_SCROLLBAR]: isPlatformSideNav },
              )}
              direction={isTopPositioned ? 'row' : 'column'}
              ref={secondaryNavRef}
              style={secondaryNavStyles}
            >
              {map(adaptedSecondaryNavItems, (item) => {
                return (
                  <NavigationItemComponent
                    activeMenuItemId={activeMenuItemId}
                    activePathnames={activePathnames}
                    item={item}
                    key={item.id}
                    setActiveMenuItemId={setActiveMenuItemId}
                    slotProps={slotProps}
                  />
                );
              })}
            </Stack>
          </>
          {userMenuItem && isPlatformSideNav ? (
            <NavigationItemComponent
              activeMenuItemId={activeMenuItemId}
              activePathnames={activePathnames}
              item={userMenuItem}
              key={userMenuItem.id}
              setActiveMenuItemId={setActiveMenuItemId}
              slotProps={slotProps}
            />
          ) : null}
        </SideNavigationWrapper>
      </Stack>
    </Stack>
  );
});

SideNavigation.displayName = 'SideNavigation';

export default SideNavigation;
